import React, { useState, useRef, useEffect } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Badge,
  Chip,
  TextField,
  Card,
  CardMedia,
  CardContent,
  CardActions
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import { useAuth } from '../../../contexts/AuthContext';
import { ImageLibraryFolderService, type ImageLibraryFolderDto } from './services/imageLibraryService';
import { useTranslation } from 'react-i18next';

interface ImageUploadModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  selectedFolder: { id: string; name: string } | null; // null ise ana dizin
  onUploadComplete?: () => void;
}

interface ImageFile {
  file: File;
  preview: string;
  name: string;
  id: string;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  open,
  onClose,
  companyId,
  selectedFolder,
  onUploadComplete
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const { currentUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<ImageFile[]>([]);
  const [editingImageId, setEditingImageId] = useState<string | null>(null);
  const [folders, setFolders] = useState<ImageLibraryFolderDto[]>([]);
  const [selectedTargetFolder, setSelectedTargetFolder] = useState<ImageLibraryFolderDto | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Maksimum resim sayısı
  const MAX_IMAGES = 10;

  // Desteklenen dosya türleri
  const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const acceptedTypes = '.jpg,.jpeg,.png,.webp';

  // Benzersiz ID oluştur
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Dosya seçimi (çoklu)
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Toplam resim sayısı kontrolü
    const totalImages = selectedImages.length + files.length;
    if (totalImages > MAX_IMAGES) {
      setError(isTr
        ? `En fazla ${MAX_IMAGES} resim yükleyebilirsiniz. Şu anda ${selectedImages.length} resminiz seçili.`
        : `You can upload up to ${MAX_IMAGES} images. You currently selected ${selectedImages.length} images.`);
      return;
    }

    const validImages: ImageFile[] = [];

    files.forEach(file => {
      // Dosya türü kontrolü
      if (!supportedTypes.includes(file.type)) {
        setError(`${file.name}: ${tt('Desteklenen dosya türleri', 'Supported file types')}: JPG, PNG, WEBP`);
        return;
      }

      // Dosya boyutu kontrolü (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError(`${file.name}: ${tt("Dosya boyutu 10MB'dan küçük olmalıdır", 'File size must be less than 10MB')}`);
        return;
      }

      // Dosya adından uzantıyı çıkar ve temiz isim oluştur
      const cleanName = file.name.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9\sÇĞÖŞÜçğöşü]/g, " ").trim();

      const imageFile: ImageFile = {
        file,
        preview: '',
        name: cleanName || `${tt('Resim', 'Image')} ${selectedImages.length + validImages.length + 1}`,
        id: generateId()
      };

      // Önizleme için FileReader kullan
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        imageFile.preview = result;
        
        validImages.push(imageFile);
        
        // Tüm dosyalar okunduysa state'i güncelle
        if (validImages.length === files.length) {
          setSelectedImages(prev => [...prev, ...validImages]);
        }
      };
      reader.readAsDataURL(file);
    });

    setError(null);
    // Input'u temizle
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Seçili resmi kaldır
  const removeImage = (id: string) => {
    setSelectedImages(prev => prev.filter(img => img.id !== id));
    setEditingImageId(null);
  };

  // Resim adını güncelle
  const updateImageName = (id: string, newName: string) => {
    setSelectedImages(prev => 
      prev.map(img => 
        img.id === id ? { ...img, name: newName.trim() || `Resim ${prev.indexOf(img) + 1}` } : img
      )
    );
  };

  // Resimleri yükle
  const handleUpload = async () => {
    if (selectedImages.length === 0 || !companyId) return;

    // İsim kontrolü
    const emptyNames = selectedImages.filter(img => !img.name.trim());
    if (emptyNames.length > 0) {
      setError(tt('Lütfen tüm resimler için isim giriniz.', 'Please enter a name for all images.'));
      return;
    }

    setUploading(true);
    setError(null);

    try {
      if (!currentUser) {
        throw new Error(tt('Kullanıcı oturumu açık değil', 'User session is not active'));
      }

      // ✅ ADIM 1: Resimleri Image Service'e yükle
      const { ImageUploadService } = await import('./services/imageUploadService');
      const files = selectedImages.map(img => img.file);
      const uploadedImages = await ImageUploadService.uploadImages(files);

      console.log('✅ Resimler Image Service\'e yüklendi:', uploadedImages);

      // ✅ ADIM 2: SQL'e resim kayıtlarını oluştur
      const { ImageLibraryImageService } = await import('./services/imageLibraryService');

      const createImagePromises = selectedImages.map(async (imageFile, index) => {
        const uploadedImage = uploadedImages[index];

        // SQL'e resim kaydı oluştur
        const imageData = {
          companyId: companyId,
          folderId: selectedTargetFolder?.id || undefined, // Ana dizin için undefined
          name: imageFile.name.trim(),
          url: uploadedImage.originalUrl, // Image Service'den dönen orijinal URL
          thumbnailUrl: uploadedImage.thumbnailUrl, // Image Service'den dönen thumbnail URL
          size: imageFile.file.size,
          type: imageFile.file.type,
          createdBy: currentUser.uid,
        };

        return await ImageLibraryImageService.createImage(imageData);
      });

      await Promise.all(createImagePromises);

      console.log('✅ Resimler SQL\'e kaydedildi');
      
      // Üst component'i bilgilendir ve yenilemeyi bekle
      if (onUploadComplete) {
        console.log('🔄 Liste yenileniyor...');
        await onUploadComplete();
        console.log('✅ Liste yenilendi');
      }

      // Başarılı upload sonrası temizle
      setSelectedImages([]);
      setError(null);
      
      // State güncellemesinin tamamlanması için kısa bir gecikme
      await new Promise(resolve => setTimeout(resolve, 100));
      
      onClose();

    } catch (error: any) {
      console.error('❌ Resim yükleme hatası:', error);
      setError(error.message || tt('Resimler yüklenirken bir hata oluştu', 'An error occurred while uploading images'));
    } finally {
      setUploading(false);
    }
  };

  // Modal kapanırken temizle
  const handleClose = () => {
    if (uploading) return;
    setSelectedImages([]);
    setError(null);
    setEditingImageId(null);
    onClose();
  };

  // Dosya seçim butonuna tıklama
  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  // Modal açıldığında temizle
  useEffect(() => {
    if (open) {
      setSelectedImages([]);
      setError(null);
      setEditingImageId(null);
      setNewFolderName('');
    }
  }, [open]);

  // Klasorleri yukle ve hedef klasoru sec
  useEffect(() => {
    const loadFolders = async () => {
      if (!open || !companyId) return;
      try {
        const data = await ImageLibraryFolderService.getAllFolders(companyId);
        setFolders(data);
        if (selectedFolder?.id) {
          const matched = data.find(f => f.id === selectedFolder.id) || null;
          setSelectedTargetFolder(matched);
        } else {
          setSelectedTargetFolder(null);
        }
      } catch {
        setFolders([]);
        setSelectedTargetFolder(null);
      }
    };
    loadFolders();
  }, [open, companyId, selectedFolder?.id]);

  // Drag & drop event listener'ı
  useEffect(() => {
    const handleDragDropFiles = (event: CustomEvent) => {
      if (open && event.detail?.files) {
        const files = event.detail.files as File[];
        processDroppedFiles(files);
      }
    };

    window.addEventListener('dragDropFiles', handleDragDropFiles as EventListener);
    
    return () => {
      window.removeEventListener('dragDropFiles', handleDragDropFiles as EventListener);
    };
  }, [open, selectedImages]);

  // Sürüklenen dosyaları işle
  const processDroppedFiles = (files: File[]) => {
    if (files.length === 0) return;

    // Toplam resim sayısı kontrolü
    const totalImages = selectedImages.length + files.length;
    if (totalImages > MAX_IMAGES) {
      setError(isTr
        ? `En fazla ${MAX_IMAGES} resim yükleyebilirsiniz. Şu anda ${selectedImages.length} resminiz seçili.`
        : `You can upload up to ${MAX_IMAGES} images. You currently selected ${selectedImages.length} images.`);
      return;
    }

    const validImages: ImageFile[] = [];

    files.forEach(file => {
      // Dosya türü kontrolü
      if (!supportedTypes.includes(file.type)) {
        setError(`${file.name}: ${tt('Desteklenen dosya türleri', 'Supported file types')}: JPG, PNG, WEBP`);
        return;
      }

      // Dosya boyutu kontrolü (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError(`${file.name}: ${tt("Dosya boyutu 10MB'dan küçük olmalıdır", 'File size must be less than 10MB')}`);
        return;
      }

      // Dosya adından uzantıyı çıkar ve temiz isim oluştur
      const cleanName = file.name.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9\sÇĞÖŞÜçğöşü]/g, " ").trim();

      const imageFile: ImageFile = {
        file,
        preview: '',
        name: cleanName || `${tt('Resim', 'Image')} ${selectedImages.length + validImages.length + 1}`,
        id: generateId()
      };

      // Önizleme için FileReader kullan
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        imageFile.preview = result;
        
        validImages.push(imageFile);
        
        // Tüm dosyalar okunduysa state'i güncelle
        if (validImages.length === files.length) {
          setSelectedImages(prev => [...prev, ...validImages]);
        }
      };
      reader.readAsDataURL(file);
    });

    setError(null);
  };

  const canAddMore = selectedImages.length < MAX_IMAGES;
  
  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name || !companyId) return;
    try {
      const created = await ImageLibraryFolderService.createFolder({
        companyId,
        name,
        createdBy: currentUser?.uid
      });
      setFolders(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'tr')));
      setSelectedTargetFolder(created);
      setNewFolderName('');
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || tt('Klasör oluşturulurken hata oluştu.', 'An error occurred while creating folder.'));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!uploading ? handleClose : undefined}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          fontWeight: 'bold',
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PhotoLibraryIcon sx={{ mr: 1 }} />
          {tt('Resim Kütüphanesine Yükle', 'Upload to Image Library')}
          <Badge badgeContent={selectedImages.length} color="secondary" sx={{ ml: 2 }}>
            <Chip 
              label={`${selectedImages.length}/${MAX_IMAGES}`} 
              size="small" 
              sx={{ bgcolor: 'white', color: 'primary.main' }}
            />
          </Badge>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={uploading}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {/* Hedef Klasör Bilgisi */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {selectedTargetFolder ? (
              <>
                <FolderIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  {tt('Hedef Klasör', 'Target Folder')}: {selectedTargetFolder.name}
                </Typography>
              </>
            ) : (
              <>
                <HomeIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  {tt('Hedef: Ana Dizin', 'Target: Main Directory')}
                </Typography>
              </>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {tt('Resimler bu konuma yüklenecektir.', 'Images will be uploaded to this location.')}
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <Autocomplete
              size="small"
              options={folders}
              getOptionLabel={(option) => option.name}
              value={selectedTargetFolder}
              onChange={(_, value) => setSelectedTargetFolder(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={tt('Hedef Dizin', 'Target Directory')}
                  placeholder={tt('Ana Dizin için boş bırakın', 'Leave empty for Main Directory')}
                  helperText={tt('Yükleme klasörünü değiştirebilirsiniz.', 'You can change the upload folder.')}
                />
              )}
            />
          </Box>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label={tt('Yeni Klasör', 'New Folder')}
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateFolder();
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={handleCreateFolder}
              disabled={!companyId || !newFolderName.trim()}
            >
              {tt('Klasör Oluştur', 'Create Folder')}
            </Button>
          </Box>
        </Box>

        {/* Hata Mesajı */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Dosya Seçim Alanı */}
        {canAddMore && (
          <Paper
            elevation={1}
            sx={{
              p: 3,
              mb: 3,
              border: '2px dashed',
              borderColor: selectedImages.length > 0 ? 'primary.main' : 'grey.300',
              bgcolor: selectedImages.length > 0 ? 'primary.light' : 'grey.50',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.light'
              }
            }}
            onClick={handleSelectFileClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptedTypes}
              onChange={handleFileSelect}
              multiple
              style={{ display: 'none' }}
            />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <AddPhotoAlternateIcon 
                sx={{ 
                  fontSize: 48, 
                  color: selectedImages.length > 0 ? 'primary.main' : 'grey.400',
                  mb: 2 
                }} 
              />
              <Typography variant="h6" gutterBottom>
                {selectedImages.length > 0 
                  ? `${selectedImages.length} ${tt('resim seçildi', 'images selected')}` 
                  : tt('Resimler Seç (Çoklu seçim yapabilirsiniz)', 'Select Images (multiple selection allowed)')
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tt('JPG, PNG, WEBP formatları desteklenir', 'JPG, PNG, WEBP formats are supported')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {tt('Maksimum dosya boyutu', 'Maximum file size')}: 10MB | {tt('Maksimum resim sayısı', 'Maximum image count')}: {MAX_IMAGES}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tt('Kalan slot', 'Remaining slot')}: {MAX_IMAGES - selectedImages.length}
              </Typography>
            </Box>
          </Paper>
        )}

        {!canAddMore && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {tt('Maksimum resim sayısına ulaştınız', 'You reached the maximum image count')} ({MAX_IMAGES}). {tt('Yeni resim eklemek için önce mevcut resimlerden bazılarını kaldırın.', 'Remove some existing images first to add new ones.')}
          </Alert>
        )}

        {/* Seçilen Resimler */}
        {selectedImages.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {tt('Seçilen Resimler', 'Selected Images')} ({selectedImages.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tt('Her resim için bir isim girmeniz gerekmektedir.', 'You need to enter a name for each image.')}
            </Typography>
            
            <Grid container spacing={2}>
              {selectedImages.map((imageFile, index) => (
                <Grid item xs={12} sm={6} md={4} key={imageFile.id}>
                  <Card elevation={2}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={imageFile.preview}
                      alt={imageFile.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ pb: 1 }}>
                      {editingImageId === imageFile.id ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={imageFile.name}
                          onChange={(e) => updateImageName(imageFile.id, e.target.value)}
                          onBlur={() => setEditingImageId(null)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              setEditingImageId(null);
                            }
                          }}
                          autoFocus
                          placeholder={tt('Resim adı giriniz...', 'Enter image name...')}
                        />
                      ) : (
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { color: 'primary.main' }
                          }}
                          onClick={() => setEditingImageId(imageFile.id)}
                          title={tt('Düzenlemek için tıklayın', 'Click to edit')}
                        >
                          {imageFile.name || `${tt('Resim', 'Image')} ${index + 1}`}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {(imageFile.file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ pt: 0, justifyContent: 'space-between' }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => setEditingImageId(imageFile.id)}
                        disabled={editingImageId === imageFile.id}
                      >
                        {tt('Düzenle', 'Edit')}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => removeImage(imageFile.id)}
                      >
                        {tt('Kaldır', 'Remove')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Resim Yok Bilgisi */}
        {selectedImages.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
            <PhotoLibraryIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {tt('Henüz resim seçilmedi', 'No image selected yet')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tt('Yukarıdaki alanı kullanarak resim seçebilirsiniz.', 'You can select images using the area above.')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          disabled={uploading}
          variant="outlined"
        >
          {tt('İptal', 'Cancel')}
        </Button>
        
        <Button
          onClick={handleUpload}
          disabled={selectedImages.length === 0 || uploading}
          variant="contained"
          startIcon={uploading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {uploading ? tt('Yükleniyor...', 'Uploading...') : `${selectedImages.length} ${tt('Resimi Kaydet', 'Save Image')}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageUploadModal; 